import { computed, ref, watch } from 'vue';
import { getSubtaskRollup } from '~/lib/subtaskRollup';
import { isPendingClientTaskWork } from '~/lib/taskClientResponse';
import type { Task } from '~/types/models';

export type DisplayTask = Task & {
  _depth: number;
  _hasChildren: boolean;
  _isSubtask: boolean;
  _rollup?: ReturnType<typeof getSubtaskRollup>;
};

interface TaskHierarchyOptions {
  tasks: () => Task[];
  storageKey: () => string;
  collapseParentsByDefault: () => boolean;
  showClientResponseState: () => boolean;
}

export function useTaskHierarchy(options: TaskHierarchyOptions) {
  const collapsedTaskIds = ref<Set<string>>(new Set());
  const initialized = ref(false);

  const taskChildrenMap = computed(() => {
    const children = new Map<string, Task[]>();
    for (const task of options.tasks()) {
      if (!task.subtaskOfId) continue;
      const siblings = children.get(task.subtaskOfId) || [];
      siblings.push(task);
      children.set(task.subtaskOfId, siblings);
    }
    return children;
  });

  const orderedTasks = computed<DisplayTask[]>(() => {
    const tasks = options.tasks();
    const rows: DisplayTask[] = [];
    const taskIds = new Set(tasks.map(task => task.id));
    const roots = tasks.filter(task => !task.subtaskOfId || !taskIds.has(task.subtaskOfId));

    const append = (task: Task, depth = 0) => {
      const children = taskChildrenMap.value.get(task.id) || [];
      const hasChildren = children.length > 0;
      rows.push({
        ...task,
        _depth: depth,
        _hasChildren: hasChildren,
        _isSubtask: depth > 0,
        _rollup: hasChildren ? getSubtaskRollup(task.id, tasks) : undefined,
      });
      if (hasChildren && collapsedTaskIds.value.has(task.id)) return;
      children.forEach(child => append(child, depth + 1));
    };

    roots.forEach(task => append(task));
    return rows;
  });

  const descendantTaskIdsMap = computed(() => {
    const descendants = new Map<string, Set<string>>();
    const collect = (taskId: string): Set<string> => {
      const cached = descendants.get(taskId);
      if (cached) return cached;
      const result = new Set<string>();
      for (const child of taskChildrenMap.value.get(taskId) || []) {
        result.add(child.id);
        collect(child.id).forEach(id => result.add(id));
      }
      descendants.set(taskId, result);
      return result;
    };
    options.tasks().forEach(task => collect(task.id));
    return descendants;
  });

  const parentTaskIds = computed(() => options.tasks()
    .filter(task => (taskChildrenMap.value.get(task.id)?.length || 0) > 0)
    .map(task => task.id));

  function readStoredState(): Record<string, boolean> | null {
    if (!options.storageKey() || typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(options.storageKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return Object.fromEntries(parsed.map(value => [String(value), true]));
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  function persist(taskIds: Set<string>) {
    if (!options.storageKey() || typeof window === 'undefined') return;
    try {
      const payload = Object.fromEntries(parentTaskIds.value.map(taskId => [taskId, taskIds.has(taskId)]));
      window.localStorage.setItem(options.storageKey(), JSON.stringify(payload));
    } catch {
      // Storage is optional; hierarchy behavior should continue without it.
    }
  }

  function hasPendingClientDescendant(taskId: string) {
    if (!options.showClientResponseState()) return false;
    const descendantIds = descendantTaskIdsMap.value.get(taskId) || new Set<string>();
    return options.tasks().some(task => descendantIds.has(task.id) && isPendingClientTaskWork(task));
  }

  function shouldCollapse(taskId: string, stored: Record<string, boolean> | null) {
    if (hasPendingClientDescendant(taskId)) return false;
    return stored
      ? (stored[taskId] ?? options.collapseParentsByDefault())
      : options.collapseParentsByDefault();
  }

  function sync(parentIds: string[]) {
    const stored = readStoredState();
    if (!initialized.value) {
      collapsedTaskIds.value = new Set(parentIds.filter(id => shouldCollapse(id, stored)));
      initialized.value = true;
      return;
    }

    const valid = new Set(parentIds);
    const next = new Set(Array.from(collapsedTaskIds.value).filter(id => valid.has(id)));
    for (const parentId of parentIds) {
      if (hasPendingClientDescendant(parentId)) next.delete(parentId);
      else if (!next.has(parentId) && shouldCollapse(parentId, stored)) next.add(parentId);
    }
    collapsedTaskIds.value = next;
  }

  function toggleTaskCollapse(taskId: string) {
    const next = new Set(collapsedTaskIds.value);
    next.has(taskId) ? next.delete(taskId) : next.add(taskId);
    collapsedTaskIds.value = next;
    persist(next);
  }

  watch(parentTaskIds, sync, { immediate: true });
  watch(() => options.storageKey(), () => {
    initialized.value = false;
    sync(parentTaskIds.value);
  });
  watch(taskChildrenMap, (nextMap, previousMap) => {
    if (!previousMap) return;
    const next = new Set(collapsedTaskIds.value);
    let changed = false;
    for (const [parentId, children] of nextMap) {
      const previousCount = previousMap.get(parentId)?.length || 0;
      if (previousCount > 0 && children.length > previousCount && next.delete(parentId)) changed = true;
    }
    if (changed) {
      collapsedTaskIds.value = next;
      persist(next);
    }
  });

  return {
    collapsedTaskIds,
    descendantTaskIdsMap,
    orderedTasks,
    toggleTaskCollapse,
  };
}
